import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { EnhancedTimelineCalendar } from "./EnhancedTimelineCalendar";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DateSliderQuestion as DateSliderQuestionType } from "./questionTypes";
import { QuestionTooltip } from "./QuestionTooltip";
interface DateSliderQuestionProps {
  question: DateSliderQuestionType;
  value: DateSliderAnswer | undefined;
  onChange: (questionId: string, value: DateSliderAnswer) => void;
}
interface DateSliderAnswer {
  type: 'date' | 'slider';
  value: Date | number;
}
export function DateSliderQuestion({
  question,
  value,
  onChange
}: DateSliderQuestionProps) {
  const [mode, setMode] = useState<'date' | 'slider'>(value?.type || 'date');
  const defaultSliderValue = question.defaultValue || 0;
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      onChange(question.id, {
        type: 'date',
        value: date
      });
    }
  };
  const handleSliderChange = (sliderValue: number[]) => {
    onChange(question.id, {
      type: 'slider',
      value: sliderValue[0]
    });
  };
  const handleModeSwitch = (newMode: 'date' | 'slider') => {
    setMode(newMode);
    if (newMode === 'slider' && (!value || value.type !== 'slider')) {
      onChange(question.id, {
        type: 'slider',
        value: defaultSliderValue
      });
    }
  };
  return <div className="space-y-8 w-full max-w-3xl mx-auto py-4">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-3">{question.title}</h2>
        
        {question.description && <p className="text-muted-foreground mb-8">{question.description}</p>}
        
        {question.tooltip && <QuestionTooltip tooltip={question.tooltip} />}
      </div>

      <div className="space-y-6">
        {/* Mode Selection */}
        <div className="flex gap-3">
          <Button variant={mode === 'date' ? 'default' : 'outline'} onClick={() => handleModeSwitch('date')} className="flex-1">Select a Specific Date</Button>
          <Button variant={mode === 'slider' ? 'default' : 'outline'} onClick={() => handleModeSwitch('slider')} className="flex-1">Use a General Timeframe</Button>
        </div>

        {/* Date Picker Mode */}
        {mode === 'date' && <div className="flex flex-col items-center space-y-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-[280px] justify-start text-left font-normal", !value || value.type !== 'date' && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value && value.type === 'date' ? format(value.value as Date, "PPP") : <span>Pick your ideal exit date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <EnhancedTimelineCalendar
                  selectedDate={value?.type === 'date' ? value.value as Date : undefined}
                  onDateSelect={handleDateSelect}
                />
              </PopoverContent>
            </Popover>
          </div>}

        {/* Slider Mode */}
        {mode === 'slider' && <div className="py-4">
            <Slider defaultValue={[defaultSliderValue]} max={question.maxValue || 10} min={question.minValue || 0} step={question.step || 1} value={[value?.type === 'slider' ? value.value as number : defaultSliderValue]} onValueChange={handleSliderChange} className="w-full" />
            
            <div className="flex justify-between mt-3 text-sm text-muted-foreground">
              <span>{question.labels?.min}</span>
              <span>{question.labels?.max}</span>
            </div>
            
            <div className="text-center mt-6">
              <span className="text-3xl font-medium text-primary">
                {value?.type === 'slider' ? value.value as number : defaultSliderValue}
              </span>
              <span className="ml-2 text-lg text-muted-foreground">
                {question.valueLabel || "years"}
              </span>
            </div>
          </div>}
      </div>
    </div>;
}